/**
 * NCR Details form body — all input rows for the Create NCR screen.
 * Extracted from NCRCreate.tsx to keep files under the 500-line limit (CR-6387).
 * Purely presentational: all state + handlers arrive via props.
 */
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Button } from '../../ui/button';
import SearchableSelect from '../../ui/SearchableSelect';
import { Loader2, Save, Search, CheckCircle2, Send } from 'lucide-react';
import NCRContainmentSection from '../NCRContainmentSection';
import { createDispositionOptions, dispositionRequired } from '../dispositionOptions';
import NCRLiabilitySection from './NCRLiabilitySection';
import RepeatIssueField from './RepeatIssueField';
import RejectQtyHint from '../RejectQtyHint';
import ShortageQtyHint from '../ShortageQtyHint';
import type { SourceOfRejection, FaultCodeGroup, FaultCodeCategory, UserProfile, Warehouse } from '../../../lib/api';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { displayPrice } from '../../../lib/unitPrice';
import type { NCRFormData, RepeatInfo } from './ncrFormTypes';
import { usePartDescriptionLookup } from './usePartDescriptionLookup';
import { DefectDescriptionCounter } from '../defectDescriptionLimit';

export type { NCRFormData, RepeatInfo } from './ncrFormTypes';

interface NCRDetailsFormProps {
  formData: NCRFormData;
  fieldErrors: Record<string, string>;
  updateField: (field: keyof NCRFormData, value: string | number | boolean | null) => void;
  onContainmentChange: (field: 'containment_required' | 'containment_qty_ok' | 'containment_qty_not_ok' | 'containment_current_stock', value: boolean | string) => void;
  suppliers: Array<{ id: number; name: string }>;
  loadingSuppliers: boolean;
  grnLinked: boolean;
  lookingUpGRN: boolean;
  doGRNLookup: (grnNumber: string, suppliersList: Array<{ id: number; name: string }>) => Promise<boolean>;
  sourcesOfRejection: SourceOfRejection[];
  faultGroups: FaultCodeGroup[];
  filteredCategories: FaultCodeCategory[];
  customerUsers: UserProfile[];
  /** CR-7347: warehouses this user may raise an NCR against. */
  warehouses: Warehouse[];
  repeatInfo: RepeatInfo | null;
  currentStock: number | null;
  saving: boolean;
  /** `raise` = create and issue in one step (B5); otherwise save a draft. */
  onSubmit: (raise?: boolean) => void;
  onCancel: () => void;
}

/**
 * Strip everything that isn't a digit.
 *
 * The quantity fields map to `PositiveIntegerField`, so a decimal point, a
 * minus sign or the `e` that `type="number"` happily accepts are all invalid.
 * Filtering on the way in keeps the box empty-able while making a bad value
 * impossible to type, rather than catching it at save time.
 *
 * @param value - raw input value
 * @returns the digits only, possibly an empty string
 */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** SearchableSelect speaks strings; the form stores ids. '' means none picked. */
function toId(value: string): number | null {
  return value ? parseInt(value, 10) : null;
}

/**
 * Buyer choices. Buyer is stored by name, so a user with no name (only an
 * email) has nothing to store and a name shared by two users is one choice.
 *
 * @param users - the customer's users
 * @returns one option per distinct, non-blank name
 */
function buyerOptions(users: UserProfile[]): Array<{ value: string; label: string }> {
  const names = users.map((u) => `${u.user.first_name} ${u.user.last_name}`.trim()).filter(Boolean);
  return Array.from(new Set(names)).map((name) => ({ value: name, label: name }));
}

export default function NCRDetailsForm({
  formData, fieldErrors, updateField, onContainmentChange,
  suppliers, loadingSuppliers, grnLinked, lookingUpGRN, doGRNLookup,
  sourcesOfRejection, faultGroups, filteredCategories, customerUsers, warehouses,
  repeatInfo, currentStock, saving, onSubmit, onCancel,
}: NCRDetailsFormProps) {
  // When the admin has made fault classification optional, drop the * markers.
  const { ncrFaultClassificationOptional, ncrCustomerReferenceLabel } = useMenuSettings();
  const { preferences } = useFormatters();
  const priceDecimals = preferences.unit_price_decimals;
  const faultRequiredMark = ncrFaultClassificationOptional ? '' : ' *';
  // CR-7137: Part Description follows the Parts Master for a typed part number.
  const lookupPartDescription = usePartDescriptionLookup(formData, updateField);
  return (
    <Card>
      <CardHeader>
        <CardTitle>NCR Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Row 1: GRN and the customer's own reference (B3: the NCR number is generated) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="grn_number">GRN No.</Label>
            <div className="flex gap-2">
              <Input
                id="grn_number"
                value={formData.grn_number}
                onChange={(e) => updateField('grn_number', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (formData.grn_number.trim()) {
                      doGRNLookup(formData.grn_number.trim(), suppliers);
                    }
                  }
                }}
                placeholder="Enter GRN number to auto-fill..."
                className={grnLinked ? 'border-green-500' : ''}
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  if (formData.grn_number.trim()) {
                    doGRNLookup(formData.grn_number.trim(), suppliers);
                  }
                }}
                disabled={lookingUpGRN || !formData.grn_number.trim()}
                title="Look up GRN to auto-populate fields"
                aria-label="Look up GRN to auto-populate fields"
              >
                {lookingUpGRN ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : grnLinked ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            {grnLinked && <p className="text-xs text-green-600 mt-1">GRN linked - fields auto-populated</p>}
          </div>
          <div>
            {/* E4: the customer's own name for it (Admin Setup > Quality). */}
            <Label htmlFor="customer_reference">{ncrCustomerReferenceLabel}</Label>
            <Input
              id="customer_reference"
              value={formData.customer_reference}
              onChange={(e) => updateField('customer_reference', e.target.value)}
              placeholder="Optional: your own reference"
              className={fieldErrors['customer_reference'] ? 'border-red-500' : undefined}
              aria-invalid={!!fieldErrors['customer_reference'] || undefined}
              aria-describedby={fieldErrors['customer_reference'] ? 'customer_reference-error' : undefined}
            />
            {fieldErrors['customer_reference'] && <p id="customer_reference-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['customer_reference']}</p>}
          </div>
        </div>

        {/* Row 2: Supplier */}
        <div>
          <Label htmlFor="supplier">Supplier *</Label>
          {/* CR-8714: every list on this form types to filter, like the rest of the app. */}
          <SearchableSelect
            id="supplier"
            options={suppliers.map((s) => ({ value: String(s.id), label: s.name }))}
            value={formData.supplier ? String(formData.supplier) : ''}
            onChange={(v) => updateField('supplier', toId(v))}
            placeholder={loadingSuppliers ? 'Loading suppliers…' : 'Select a supplier...'}
            disabled={loadingSuppliers}
            invalid={!!fieldErrors['supplier']}
            describedBy={fieldErrors['supplier'] ? 'supplier-error' : undefined}
          />
          {fieldErrors['supplier'] && <p id="supplier-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['supplier']}</p>}
        </div>

        {/* Row 3: PO No. and PO Line No. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="po_number">PO No.</Label>
            <Input id="po_number" value={formData.po_number} onChange={(e) => updateField('po_number', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="po_line_number">PO Line No.</Label>
            <Input
              id="po_line_number"
              value={formData.po_line_number}
              onChange={(e) => updateField('po_line_number', e.target.value)}
              placeholder={grnLinked ? '' : 'Auto-filled from GRN'}
              readOnly={grnLinked}
              className={grnLinked ? 'bg-muted cursor-not-allowed' : ''}
            />
          </div>
        </div>

        {/* Row 4: Part Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="part_number">Part No.</Label>
            <Input
              id="part_number"
              value={formData.part_number}
              onChange={(e) => updateField('part_number', e.target.value)}
              onBlur={() => lookupPartDescription(formData.part_number)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupPartDescription(formData.part_number); } }}
              className={fieldErrors['part_number'] ? 'border-red-500' : undefined}
              aria-invalid={!!fieldErrors['part_number'] || undefined}
              aria-describedby={fieldErrors['part_number'] ? 'part_number-error' : undefined}
            />
            {fieldErrors['part_number'] && <p id="part_number-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['part_number']}</p>}
          </div>
          <div>
            <Label htmlFor="batch_number">Date Code / Batch No.</Label>
            <Input id="batch_number" value={formData.batch_number} onChange={(e) => updateField('batch_number', e.target.value)} />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Part Description</Label>
          <Textarea id="description" value={formData.description} onChange={(e) => updateField('description', e.target.value)} rows={2} />
        </div>

        {/* Row 5: Unit price and works order */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="unit_price">Unit Price</Label>
            {/* `step="any"` deliberately: Decimal Precision sets the default a
                prefilled price arrives at, it does not cap what can be typed.
                A fixed step would make the browser reject 10.375 as invalid on
                a company configured for 2. */}
            <Input
              id="unit_price"
              type="number"
              step="any"
              min="0"
              value={formData.unit_price}
              onChange={(e) => updateField('unit_price', e.target.value)}
              placeholder={displayPrice(0, priceDecimals)}
            />
          </div>
          <div>
            <Label htmlFor="works_order">Works Order No.</Label>
            <Input id="works_order" value={formData.works_order} onChange={(e) => updateField('works_order', e.target.value)} placeholder="Optional" />
          </div>
        </div>

        {/* CR-7347: the receiving warehouse. Stored on the NCR rather than
            derived from a GRN, so a manually raised NCR — which has no GRN —
            still appears under its warehouse in the register filter. The GRN
            lookup pre-fills it; it stays editable either way. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="warehouse">Warehouse</Label>
            <SearchableSelect
              id="warehouse"
              options={warehouses.map((w) => ({ value: String(w.id), label: w.code ? `${w.code} - ${w.name}` : w.name }))}
              value={formData.warehouse ? String(formData.warehouse) : ''}
              onChange={(v) => updateField('warehouse', toId(v))}
              placeholder={warehouses.length === 0 ? 'No warehouses configured' : 'Select...'}
              disabled={warehouses.length === 0}
              invalid={!!fieldErrors['warehouse']}
              describedBy={fieldErrors['warehouse'] ? 'warehouse-error' : undefined}
            />
            {fieldErrors['warehouse'] && (
              <p id="warehouse-error" className="text-xs text-red-600 mt-1">{fieldErrors['warehouse']}</p>
            )}
            {grnLinked && formData.warehouse !== null && (
              <p className="text-xs text-muted-foreground mt-1">Pre-filled from the linked GRN.</p>
            )}
          </div>
        </div>

        {/* Row 6: Classification. CR-8715: Source of Rejection is optional;
            Fault Group/Category are required unless the admin made them optional. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="source_of_rejection_option">Source of Rejection</Label>
            <SearchableSelect
              id="source_of_rejection_option"
              options={sourcesOfRejection.map((s) => ({ value: String(s.id), label: s.name }))}
              value={formData.source_of_rejection_option ? String(formData.source_of_rejection_option) : ''}
              onChange={(v) => updateField('source_of_rejection_option', toId(v))}
              placeholder={sourcesOfRejection.length > 0 ? 'Select...' : 'None set up'}
              invalid={!!fieldErrors['source_of_rejection_option']}
              describedBy={fieldErrors['source_of_rejection_option'] ? 'source_of_rejection_option-error' : undefined}
            />
            {fieldErrors['source_of_rejection_option'] && <p id="source_of_rejection_option-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['source_of_rejection_option']}</p>}
          </div>
          <div>
            <Label htmlFor="fault_group">Fault Group{faultRequiredMark}</Label>
            <SearchableSelect
              id="fault_group"
              options={faultGroups.map((g) => ({ value: String(g.id), label: g.name }))}
              value={formData.fault_group ? String(formData.fault_group) : ''}
              onChange={(v) => updateField('fault_group', toId(v))}
              placeholder="Select..."
              invalid={!!fieldErrors['fault_group']}
              describedBy={fieldErrors['fault_group'] ? 'fault_group-error' : undefined}
            />
            {fieldErrors['fault_group'] && <p id="fault_group-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['fault_group']}</p>}
          </div>
          <div>
            <Label htmlFor="fault_category">Fault Category{faultRequiredMark}</Label>
            <SearchableSelect
              id="fault_category"
              options={filteredCategories.map((c) => ({ value: String(c.id), label: c.name }))}
              value={formData.fault_category ? String(formData.fault_category) : ''}
              onChange={(v) => updateField('fault_category', toId(v))}
              placeholder={formData.fault_group ? 'Select...' : 'Select fault group first'}
              disabled={!formData.fault_group}
              invalid={!!fieldErrors['fault_category']}
              describedBy={fieldErrors['fault_category'] ? 'fault_category-error' : undefined}
            />
            {fieldErrors['fault_category'] && <p id="fault_category-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['fault_category']}</p>}
          </div>
        </div>

        {/* Repeat Issue — CR-8716: the raiser's Yes/No, pre-filled when this
            part + fault group was already raised on a previous claim. */}
        <RepeatIssueField
          value={formData.is_repeat_issue}
          onChange={(v) => updateField('is_repeat_issue', v)}
          repeatInfo={repeatInfo}
        />

        {/* Row 7: Buyer */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="buyer_name">Buyer</Label>
            <SearchableSelect
              id="buyer_name"
              options={buyerOptions(customerUsers)}
              value={formData.buyer_name}
              onChange={(v) => updateField('buyer_name', v)}
              placeholder="Select Buyer..."
            />
          </div>
        </div>

        {/* Row 8: Liability & Requirements */}
        <NCRLiabilitySection formData={formData} fieldErrors={fieldErrors} updateField={updateField} customerUsers={customerUsers} />

        {/* B5: the disposition the submit requires whenever a claim applies,
            asked here so the NCR can be raised in one step. */}
        {dispositionRequired(formData) && (
          <div className="max-w-xs">
            <Label htmlFor="proposed_rejection_type">Proposed Disposition *</Label>
            {/* 0 is "Please Select"; it becomes the placeholder row rather
                than an option, so picking nothing still submits 0. CR-8715:
                the options follow the liability and Collection choices. */}
            <SearchableSelect
              id="proposed_rejection_type"
              options={createDispositionOptions(formData)
                .filter((o) => o.value !== 0)
                .map((o) => ({ value: String(o.value), label: o.label }))}
              value={formData.proposed_rejection_type ? String(formData.proposed_rejection_type) : ''}
              onChange={(v) => updateField('proposed_rejection_type', toId(v) ?? 0)}
              placeholder="Please Select"
              invalid={!!fieldErrors['proposed_rejection_type']}
              describedBy={fieldErrors['proposed_rejection_type'] ? 'proposed_rejection_type-error' : undefined}
            />
            {fieldErrors['proposed_rejection_type'] && <p id="proposed_rejection_type-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['proposed_rejection_type']}</p>}
          </div>
        )}

        {/* Row 9: Quantities */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="batch_qty">Batch Qty</Label>
            <Input id="batch_qty" inputMode="numeric" placeholder="0" value={formData.batch_qty} onChange={(e) => updateField('batch_qty', digitsOnly(e.target.value))} />
          </div>
          <div>
            <Label htmlFor="reject_qty">Reject Qty</Label>
            <Input id="reject_qty" inputMode="numeric" placeholder="0" value={formData.reject_qty} onChange={(e) => updateField('reject_qty', digitsOnly(e.target.value))} />
            <RejectQtyHint grnId={formData.grn} rejectQty={formData.reject_qty} batchQty={formData.batch_qty} />
          </div>
          <div>
            <Label htmlFor="shortage_qty">Shortage Qty</Label>
            <Input id="shortage_qty" inputMode="numeric" placeholder="0" value={formData.shortage_qty} onChange={(e) => updateField('shortage_qty', digitsOnly(e.target.value))} />
            <ShortageQtyHint grnId={formData.grn} shortageQty={formData.shortage_qty} />
          </div>
        </div>

        {/* Stock Containment (customer review 2026-07). Asks the supplier, so
            not on an internal NCR (#5), nor on any customer-liability NCR,
            where the supplier isn't at fault (E26, 29 Sep 2026). */}
        {!formData.is_internal && formData.liability_type !== 2 && <NCRContainmentSection
          containmentRequired={formData.containment_required}
          qtyOk={formData.containment_qty_ok}
          qtyNotOk={formData.containment_qty_not_ok}
          currentStock={currentStock}
          manualStock={formData.containment_current_stock}
          onChange={onContainmentChange}
          errorOk={fieldErrors['containment_qty_ok']}
          errorNotOk={fieldErrors['containment_qty_not_ok']}
        />}

        {/* Defect Description */}
        <div>
          <Label htmlFor="defect_description">Defect Description *</Label>
          <Textarea
            id="defect_description"
            value={formData.defect_description}
            onChange={(e) => updateField('defect_description', e.target.value)}
            rows={4}
            placeholder="Describe the defect or non-conformance..."
            className={fieldErrors['defect_description'] ? 'border-red-500' : undefined}
            aria-invalid={!!fieldErrors['defect_description'] || undefined}
            aria-describedby={fieldErrors['defect_description'] ? 'defect_description-error' : undefined}
          />
          <DefectDescriptionCounter value={formData.defect_description} />
          {fieldErrors['defect_description'] && <p id="defect_description-error" role="alert" className="text-xs text-red-600 mt-1">{fieldErrors['defect_description']}</p>}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button variant="outline" onClick={() => onSubmit(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />Save as draft
          </Button>
          <Button onClick={() => onSubmit(true)} disabled={saving}>
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Raising...</>
            ) : (
              <><Send className="h-4 w-4 mr-2" />Raise NCR</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
